import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';

export default function SignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (!ageConfirmed) {
      setError('You must confirm that you are 19 years of age or older.');
      return;
    }
    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy to create an account.');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/auth/signup', {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        base_currency: 'USD',
        country_code: 'US',
        terms_accepted: true,
        age_confirmed: true,
      });

      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-green-200 dark:border-green-900">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-['Outfit']">Check Your Email!</CardTitle>
            <CardDescription>
              We&apos;ve sent a verification link to <strong>{formData.email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                Please check your email and click the verification link to activate your account.
                You&apos;ll be redirected to the login page shortly.
              </AlertDescription>
            </Alert>
            <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-lg font-bold text-primary-foreground">💰</span>
            </div>
            <span className="text-2xl font-bold font-['Outfit']">TheMoneyMap</span>
          </div>
          <CardTitle className="text-2xl font-['Outfit']">Create your account</CardTitle>
          <CardDescription>Start managing your finances today — 90-day free trial, no credit card required</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={loading}
                data-testid="signup-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                data-testid="signup-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                data-testid="signup-password"
              />
              <p className="text-xs text-muted-foreground">
                8+ characters with uppercase, lowercase, number, and special character
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading}
                data-testid="signup-confirm-password"
              />
            </div>

            {/* Privacy summary */}
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" />
                Privacy at a glance
              </div>
              <ul className="text-xs text-muted-foreground space-y-0.5 ml-5 list-disc">
                <li>We collect only your email and the financial data you enter.</li>
                <li>We never sell your data or share it with advertisers.</li>
                <li>You can delete your account and all data at any time from Settings.</li>
              </ul>
            </div>

            {/* Age declaration */}
            <div className="flex items-start gap-3 py-1">
              <Checkbox
                id="age-confirmed"
                checked={ageConfirmed}
                onCheckedChange={setAgeConfirmed}
                disabled={loading}
                data-testid="signup-age-checkbox"
              />
              <Label htmlFor="age-confirmed" className="text-sm leading-snug cursor-pointer font-normal">
                I confirm that I am <strong>19 years of age or older</strong>. TheMoneyMap is available to adults only.
              </Label>
            </div>

            {/* Terms & privacy acceptance */}
            <div className="flex items-start gap-3 py-1">
              <Checkbox
                id="terms-accepted"
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                disabled={loading}
                data-testid="signup-terms-checkbox"
              />
              <Label htmlFor="terms-accepted" className="text-sm leading-snug cursor-pointer font-normal">
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" className="text-blue-500 hover:underline font-medium">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" className="text-blue-500 hover:underline font-medium">
                  Privacy Policy
                </Link>.
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !termsAccepted || !ageConfirmed}
              data-testid="signup-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Sign in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
